/**
 * Core type definitions for EstateVision AI
 */

import type { EditType, IntentClassification } from './floorplanClassification';

// ============================================================================
// Aspect Ratio Types
// ============================================================================

export type AspectRatio = '1:1' | '4:3';

// ============================================================================
// Staging Style Types
// ============================================================================

export type StagingStyle = 'modern' | 'luxury' | 'industrial';

// ============================================================================
// Hero Overlay Types
// ============================================================================

export type HeroTemplateId = 'klassisch' | 'modern';

export interface HeroOverlayConfig {
  templateId: HeroTemplateId;
  badgeText: string;
  badgeEnabled: boolean;
  location: string;
  propertyType: string;
  area: string;
  rooms: string;
  price: string;
  showLocation: boolean;
  showPropertyType: boolean;
  showArea: boolean;
  showRooms: boolean;
  showPrice: boolean;
  accentColor: string;
  fontFamily: string;
  overlayScale: number; // 0.5–4.0, default 2.0
  badgeScale: number; // 0.5–2.0, default 1.0
}

// ============================================================================
// Project Types
// ============================================================================

export interface Project {
  id: string;
  userId?: string; // Creator ID for admin team view
  name: string;
  address: string;
  createdAt: number;
  thumbnailUrl?: string; // Preview image URL
  aspectRatio?: AspectRatio; // Aspect ratio for image generation
  stagingStyle?: StagingStyle; // Interior design style for staging
  uploadToken?: string;
  isUploadActive?: boolean;
  tenantUploadCount?: number;
  // Property details (optional)
  propertyType?: string | null;
  propertyAreaSqm?: number | null;
  propertyRooms?: number | null;
  propertyPrice?: string | null;
  propertyStatus?: string | null;
}

// ============================================================================
// Room Types (now using IDs that match roomConfig.ts)
// ============================================================================

export enum RoomType {
  LIVING = 'living_room',
  BEDROOM = 'bedroom',
  KITCHEN = 'kitchen',
  BATHROOM = 'bathroom',
  DINING = 'dining',
  OFFICE = 'office',
  HALLWAY = 'hallway',
  STAIRCASE = 'staircase',
  CLOSET = 'walk_in_closet',
  FACADE = 'facade',
  GARDEN = 'garden',
  BALCONY = 'balcony',
  TERRACE = 'terrace',
  ENTRANCE_OUTDOOR = 'entrance_outdoor',
  CUSTOM = 'custom',
}

// ============================================================================
// Image Types
// ============================================================================

export interface UploadedImage {
  id: string;
  file?: File; // Optional for images loaded from database
  previewUrl: string;
  roomType: RoomType;
  customLabel?: string;
  keepKitchen: boolean;
  // Database/Storage fields (when uploaded to Supabase)
  dbId?: string;
  storagePath?: string;
  // Upload status
  isUploading?: boolean;
  uploadError?: string;
  // Source tracking
  isFromDatabase?: boolean; // True if loaded from DB (e.g., tenant uploads)
  isTenantUpload?: boolean; // True if uploaded by tenant
  // Generation mode per image
  generationMode?: 'both' | 'staged-only' | 'empty-only' | 'none';
}

export enum GenerationType {
  EMPTY = 'Empty',
  STAGED = 'Staged',
}

export interface GeneratedVersion {
  id: string;
  url: string;
  timestamp: number;
  feedbackUsed?: string;
  storagePath?: string; // Supabase storage path
}

export interface GenerationVariant {
  id: string;
  url: string;
  storagePath: string;
  modelUsed?: string; // Which Gemini model generated this variant
  promptVariant?: string; // 'A' or 'B' from A/B test
}

export interface CatalogPlan {
  renderPrompt: string;
  pieces: Array<{ pieceId: string; renderImageIndex: number }>;
}

export interface CatalogDebug {
  ran: boolean;
  used: boolean;
  candidateCount: number;
  selected: Array<{ category: string; name: string; positionHint: string }>;
  notes: string;
  renderPrompt: string | null;
  fallbackReason: string | null;
  effectiveConsultPrompt?: string;
  effectiveRenderTemplate?: string;
  plan?: CatalogPlan;
  // Leerräum-Berater (empty_consult, admin-only): finales Leer-Prompt + Analysten-Diagnose.
  effectiveEmptyPrompt?: string;
  emptyConsult?: { base: string; output: string; model: string; usedOverride: boolean };
}

export interface GenerationResult {
  originalImageId: string;
  dbImageId?: string; // Database ID for linking generated images
  type: GenerationType;
  currentUrl: string | null;
  history: GeneratedVersion[];
  isLoading: boolean;
  isSaving?: boolean; // True while uploading to Supabase
  error?: string;
  storagePath?: string; // Path in Supabase storage
  promptUsed?: string; // The prompt sent to Gemini (admin debugging)
  variants?: GenerationVariant[]; // Parallel-generated variants
  selectedVariantId?: string; // Currently selected variant
  abTestId?: string; // ID of A/B test that generated these variants
  catalogDebug?: CatalogDebug; // Admin-only: Katalog-Staging-Diagnose (gewählte Möbel, Prompt, Fallback-Grund)
  verification?: {
    ran: boolean;
    needsCorrection: boolean;
    correctionApplied: boolean;
    reason?: string;
  };
}

export interface UpscaleResult {
  imageId: string;
  variant: 'staged' | 'empty';
  sourceUrl: string;
  isUpscaling: boolean;
  freepikTaskId?: string;
  upscaledUrl?: string;
  error?: string;
}

// ============================================================================
// Gallery Image Type (for ProjectDetailsView)
// ============================================================================

export interface GalleryImage {
  id: string;
  roomType: string;
  customLabel?: string;
  originalUrl?: string;
  emptyUrl?: string;
  stagedUrl?: string;
  brandedUrl?: string;
  brandedEmptyUrl?: string; // New: branded empty version
  upscaledStagedUrl?: string;
  upscaledEmptyUrl?: string;
  heroUrl?: string;
  heroOverlayConfig?: HeroOverlayConfig | null;
  createdAt: string;
  isTenantUpload?: boolean;
  stagedStoragePath?: string;
  brandedStoragePath?: string;
  emptyStoragePath?: string;
}

// ============================================================================
// Branding Types
// ============================================================================

export type LogoPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type WatermarkPosition = 'center' | 'tile' | 'tl' | 'tr' | 'bl' | 'br';

export interface BrandingConfig {
  logo: File | null;
  logoUrl: string | null;
  overlay: File | null;
  overlayUrl: string | null;
  logoPosition: LogoPosition;
  // Watermark settings
  watermarkUrl: string | null;
  watermarkOpacity: number; // 0-100, default 30
  watermarkPosition: WatermarkPosition; // default 'center'
  watermarkScale: number; // 0.1-1.0, default 0.5
}

// ============================================================================
// App State Types
// ============================================================================

// ============================================================================
// Project Member Types
// ============================================================================

export interface ProjectMember {
  memberId: string;
  memberEmail: string;
  memberName: string | null;
  avatarPath: string | null;
  assignedAt: string;
}

// ============================================================================
// App State Types
// ============================================================================

export type AppView =
  | 'dashboard'
  | 'project-details'
  | 'workspace'
  | 'wizard'
  | 'setup'
  | 'upload'
  | 'review'
  | 'upscale'
  | 'branding'
  | 'overlay-tool'
  | 'overlay-builder'
  | 'settings'
  | 'credits'
  | 'admin'
  | 'affiliate'
  | 'help'
  | 'floorplan-upload'
  | 'floorplan-unit-selection'
  | 'floorplan-labels'
  | 'floorplan-editor'
  | 'floorplan-review'
  | 'floorplan-3d'
  | 'floorplan-branding';

// ============================================================================
// Overlay Builder Types
// ============================================================================

export type OverlayTemplateId = 'bar' | 'corner' | 'frame';

export interface OverlayBuilderConfig {
  templateId: OverlayTemplateId;
  accentColor: string;
  logoUrl: string | null;
  logoPosition: LogoPosition;
  logoScale: number;
  text: string;
  fontFamily: string;
  aspectRatio: AspectRatio;
  barHeight?: number;    // 0.05-0.20, fraction of canvas height (bar template only)
  barOpacity?: number;   // 0.3-1.0 (bar template only)
  textScale?: number;    // 0.5-2.0, multiplier for text size
}

// ============================================================================
// Branding Asset Types (Library)
// ============================================================================

export type BrandingAssetType = 'logo' | 'overlay' | 'watermark';

export interface BrandingAsset {
  id: string;
  userId: string;
  type: BrandingAssetType;
  name: string;
  storagePath: string;
  publicUrl: string;
  createdAt: string;
}

export interface DbBrandingAsset {
  id: string;
  user_id: string;
  type: BrandingAssetType;
  name: string;
  storage_path: string;
  created_at: string;
}

export type WorkflowStep = 'setup' | 'upload' | 'review' | 'upscale' | 'branding';

// ============================================================================
// Floor Plan Types
// ============================================================================

export type FloorplanFlooring = 'wood' | 'stone' | 'tile' | 'parquet' | 'carpet';
export type FloorplanTextHandling = 'remove' | 'keep';
export type FloorplanGenerationMode = '2d_generation' | '3d_generation';

// ============================================================================
// Prompt Configuration Types
// ============================================================================

export type PromptCategory =
  | 'style'
  | 'constraint'
  | 'aspect_ratio'
  | 'system'
  | 'room_empty'
  | 'room_staged'
  | 'verification'
  | 'stage_refinement'
  | 'empty_consult'
  | 'floorplan_2d'
  | 'floorplan_3d'
  | 'floorplan_refinement'
  | 'floorplan_flooring'
  | 'floorplan_text_handling'
  | 'floorplan_room_label'
  | 'floorplan_refinement_clause';

// ============================================================================
// Provider-specific parameter types
// ============================================================================

export interface OpenAIParams {
  quality?: 'low' | 'medium' | 'high' | 'auto';
}

// Fixture block-outs (visual bounding boxes for AI)
export type FixtureType = 'shower' | 'bathtub' | 'sink' | 'toilet' | 'kitchen_counter' | 'kitchen_island';

export interface FloorplanFixture {
  id: string;
  type: FixtureType;
  label: string;     // e.g., "DUSCHE" — burned into image for AI OCR
  x: number;         // relative 0-1
  y: number;         // relative 0-1
  width: number;     // relative 0-1
  height: number;    // relative 0-1
  color: string;     // e.g., 'rgba(59, 130, 246, 0.5)'
  rotation?: number; // degrees, default 0 (used for vector-symbol burn-in + preview)
}

// Per-area flooring
export type FloorplanAreaGroup = 'living' | 'kitchen' | 'bathroom' | 'outdoor';
export type AreaFlooring = Partial<Record<FloorplanAreaGroup, FloorplanFlooring>>;

export interface FloorplanHistoryEntry {
  id: string;
  storagePath: string;
  url: string;
  createdAt: string;
}

// ============================================================================
// Phase D.2 — Floorplan Version / Message Tracking
// ============================================================================

export type FloorplanMessageStatus = 'pending' | 'success' | 'failed' | 'awaiting_user_action';

export interface BoxCoordinates {
  x1: number; // normalized 0..1 within image dims
  y1: number;
  x2: number;
  y2: number;
}

export interface FloorplanVersion {
  id: string;
  imageId: string;
  versionNumber: number;
  initialStoragePath: string;
  createdAt: string;
}

export interface FloorplanMessage {
  id: string;
  versionId: string;
  messageNumber: number;
  userPrompt: string;
  boxCoordinates: BoxCoordinates | null;
  resultingStoragePath: string | null;
  openaiResponseId: string | null;
  creditsCharged: number;
  status: FloorplanMessageStatus;
  errorMessage: string | null;
  createdAt: string;
}

export interface FloorplanRoomLabel {
  id: string;
  type: string;
  label: string;
  x: number;
  y: number;
}

export interface FloorplanConfig {
  flooring: FloorplanFlooring;
  textHandling: FloorplanTextHandling;
  roomsLabeled: boolean;
  roomLabels: FloorplanRoomLabel[];
  reviewLabels?: FloorplanRoomLabel[];
  fixtures?: FloorplanFixture[];
  areaFlooring?: AreaFlooring;
}

/** Vision-Analyst fixture plan returned by generate-floorplan (2D). */
export interface FloorplanFixturePlan {
  rooms: Array<{
    room_type: 'bathroom' | 'kitchen';
    location?: string;
    description?: string;
    fixtures: Array<{ type: string; position_hint: string }>;
  }>;
  notes: string;
}

/** Admin analyst preview result (generate-floorplan previewOnly response). */
export interface FloorplanAnalyzeResult {
  fixturePlan: FloorplanFixturePlan | null;
  assembledPrompt: string;
  effectiveConsultPrompt: string;
  effectiveBlockTemplate: string;
}

/**
 * Admin-only refinement dry-run (generate-floorplan-refinement previewOnly
 * response). Mirrors the edge function's `PreviewResponse` — classification
 * and its derived fields are null when the classifier was skipped or failed.
 */
export interface FloorplanRefinementPreview {
  success: true;
  preview: true;
  classification: IntentClassification | null;
  region_description: string | null;
  operation_instruction: string | null;
  edit_type: EditType | null;
  templateKey: string | null;
  finalPrompt: string;
  model: string;
  needsReference: boolean;
  classifierPrompt: string;
  template: string;
}

export interface FloorplanTransform {
  rotation: number; // degrees, 0 = none
  scale: number;    // multiplier, 1 = contain-fit at Auto-Init
  tx: number;       // frame-relative offset, 0..1 (0.5 = centered)
  ty: number;       // frame-relative offset, 0..1 (0.5 = centered)
}

export interface FloorplanArrow {
  /** Frame-relative X center, 0..1. */
  x: number;
  /** Frame-relative Y center, 0..1. */
  y: number;
  /** Size as fraction of frame.width (e.g. 0.08 = 8% of frame width). */
  size: number;
  /** Rotation in degrees; 0 = triangle tip points up (north). */
  rotation: number;
}

export interface FloorplanState {
  imageDbId: string | null;
  originalStoragePath: string | null;
  previewUrl: string | null;
  config: FloorplanConfig;

  // 2D output
  generated2dUrl: string | null;
  generated2dPath: string | null;
  is2dGenerating: boolean;
  generation2dError: string | null;

  // 3D output
  generated3dUrl: string | null;
  generated3dPath: string | null;
  is3dGenerating: boolean;
  generation3dError: string | null;

  // Branding transforms (null → Auto-Init runs on next Branding visit)
  transform2d: FloorplanTransform | null;
  transform3d: FloorplanTransform | null;

  // Entrance arrow overlays (null = no arrow placed)
  arrow2d: FloorplanArrow | null;
  arrow3d: FloorplanArrow | null;

  // Branded composite outputs (populated by Save in BrandingView)
  branded2dUrl: string | null;
  branded2dPath: string | null;
  branded3dUrl: string | null;
  branded3dPath: string | null;

  // Unit extraction (unchanged)
  isUnitExtracting: boolean;
  unitExtractionError: string | null;
  unitExtractionDone: boolean;
  extractedUnitUrl: string | null;
  extractedUnitPath: string | null;
  unitPolygonPoints: Array<{ x: number; y: number }>;
  lastExtractedPolygonKey: string | null;

  // Generation history (unchanged)
  floorplanHistory: FloorplanHistoryEntry[];
  isHistoryLoading: boolean;

  // Phase D.2 — version/message tracking
  sourceStoragePath: string | null;    // Original upload, never overwritten by unit-extraction
  activeVersionId: string | null;      // Which FloorplanVersion is currently displayed
  activeMessageNumber: number | null;  // Which mini-version (message) within the active version; null = Ausgangs-Stand
  versions: FloorplanVersion[];        // All versions loaded from DB
  messagesByVersion: Record<string, FloorplanMessage[]>; // Keyed by versionId
}

export const INITIAL_FLOORPLAN_STATE: FloorplanState = {
  imageDbId: null,
  originalStoragePath: null,
  previewUrl: null,
  config: {
    flooring: 'wood',
    textHandling: 'remove',
    roomsLabeled: false,
    roomLabels: [],
  },
  generated2dUrl: null,
  generated2dPath: null,
  is2dGenerating: false,
  generation2dError: null,
  generated3dUrl: null,
  generated3dPath: null,
  is3dGenerating: false,
  generation3dError: null,
  transform2d: null,
  transform3d: null,
  arrow2d: null,
  arrow3d: null,
  branded2dUrl: null,
  branded2dPath: null,
  branded3dUrl: null,
  branded3dPath: null,
  isUnitExtracting: false,
  unitExtractionError: null,
  unitExtractionDone: false,
  extractedUnitUrl: null,
  extractedUnitPath: null,
  unitPolygonPoints: [],
  lastExtractedPolygonKey: null,
  floorplanHistory: [],
  isHistoryLoading: false,
  sourceStoragePath: null,
  activeVersionId: null,
  activeMessageNumber: null,
  versions: [],
  messagesByVersion: {},
};

// ============================================================================
// API Types
// ============================================================================

export interface GenerationOptions {
  file: File;
  roomType: RoomType;
  generationType: GenerationType;
  keepKitchen: boolean;
  feedback?: string;
  customLabel?: string;
}

export interface GenerationResponse {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

// ============================================================================
// Database Types
// ============================================================================

export interface DbProject {
  id: string;
  user_id: string;
  name: string;
  street: string;
  created_at: string;
  aspect_ratio?: AspectRatio;
  staging_style?: StagingStyle;
  upload_token?: string;
  is_upload_active?: boolean;
  tenant_upload_count?: number;
  property_type?: string | null;
  property_area_sqm?: number | null;
  property_rooms?: number | null;
  property_price?: string | null;
  property_status?: string | null;
}

export interface ImageHistoryEntry {
  id: string;
  image_id: string;
  version_type: 'Empty' | 'Staged' | 'Floorplan2D' | 'Floorplan3D';
  storage_path: string;
  created_at: string;
}

export interface DbImage {
  id: string;
  project_id: string;
  storage_path: string;
  room_type: string;
  keep_kitchen: boolean;
  custom_label?: string;
  created_at: string;
  storage_path_empty?: string;
  storage_path_staged?: string;
  storage_path_branded?: string;
  storage_path_branded_empty?: string; // New: branded empty path
  storage_path_upscaled_staged?: string;
  storage_path_upscaled_empty?: string;
  storage_path_hero?: string;
  hero_overlay_config?: HeroOverlayConfig | null;
  storage_path_floorplan_2d?: string;
  storage_path_floorplan_3d?: string;
  storage_path_floorplan_2d_branded?: string | null;
  storage_path_floorplan_3d_branded?: string | null;
  floorplan_2d_transform?: FloorplanTransform | null;
  floorplan_3d_transform?: FloorplanTransform | null;
  floorplan_2d_entry_arrow?: FloorplanArrow | null;
  floorplan_3d_entry_arrow?: FloorplanArrow | null;
  floorplan_config?: FloorplanConfig | null;
  // D.2: version/message tracking
  source_storage_path?: string | null;
  active_version_id?: string | null;
  active_message_number?: number | null;
  status?: 'pending' | 'processing' | 'completed' | 'error' | 'uploaded_by_tenant';
  // Optional: loaded history entries
  history?: ImageHistoryEntry[];
}

// ============================================================================
// Demo Pairs Types
// ============================================================================

export interface DemoPair {
  id: string;
  inputStoragePath: string;
  inputPhash: string;
  outputStoragePath: string;
  method: 'empty' | 'staged';
  label: string | null;
  userIds: string[];
  createdAt: string;
  inputUrl: string;
  outputUrl: string;
}

// ============================================================================
// Admin Dashboard Types
// ============================================================================

export interface AdminUserStats {
  user_id: string;
  email: string | null;
  full_name: string | null;
  credits: number;
  is_admin: boolean;
  registered_at: string;
  last_sign_in_at: string | null;
  total_images_generated: number;
  project_count: number;
  total_spent_cents: number;
  total_credits_purchased: number;
  last_transaction_at: string | null;
  floorplan_singles: number;
  floorplan_quota_month: number;
}

export interface AdminKPIs {
  totalUsers: number;
  activeUsers: number; // Last 30 days
  totalCreditsUsed: number;
  totalImagesGenerated: number;
  totalRevenue: number; // In cents
}

export interface ProductUpdate {
  id: string;
  title: string;
  bodyMd: string;
  coverImageUrl: string | null;
  youtubeUrl: string | null;
  published: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FurniturePiece {
  id: string;
  category: string;
  name: string;
  storage_path: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MoodboardEntry {
  itemId: string;
  pieceId: string;
  category: string;
  name: string;
  url: string;
  position: number;
}

export interface MoodboardHeader {
  id: string;
  name: string;
  isActive: boolean;
}

export interface RequiredCategoryRow {
  room_type: string;
  category: string;
}

// ============================================================================
// Tutorials & Support-Chat
// ============================================================================

export interface TutorialVideo {
  id: string;
  title: string;
  description: string | null;
  youtubeVideoId: string;
  category: string | null;
  sortOrder: number;
  showOnSignup: boolean;
  published: boolean;
  createdAt: string;
}

export type SupportTicketStatus = 'open' | 'answered' | 'closed';

export interface SupportTicket {
  id: string;
  userId: string;
  status: SupportTicketStatus;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  senderRole: 'user' | 'admin';
  body: string;
  readByUser: boolean;
  readByAdmin: boolean;
  createdAt: string;
}
